"use client";

import type { WorkspaceCapabilityOverview } from "@/modules/auth/company/companyCapabilities.types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "@/contexts/AuthProvider";
import { getCurrentWorkspaceCapabilities } from "@/modules/auth/company/secondary/companyCapabilities.secondary";

type WorkspaceCapabilitiesContextValue = {
  capabilities?: WorkspaceCapabilityOverview;
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
};

const WorkspaceCapabilitiesContext =
  createContext<WorkspaceCapabilitiesContextValue>({
    loading: false,
    refresh: async () => undefined,
  });

export function WorkspaceCapabilitiesProvider({ children }) {
  const { me } = useAuth();
  const [capabilities, setCapabilities] =
    useState<WorkspaceCapabilityOverview>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    if (!me?.companyId) {
      setCapabilities(undefined);
      setError(undefined);
      return;
    }
    setLoading(true);
    const response = await getCurrentWorkspaceCapabilities();
    setLoading(false);
    if (!response.isSuccess || !response.result) {
      setError(response.error ?? "Workspace capabilities could not be loaded.");
      return;
    }
    setCapabilities(response.result);
    setError(undefined);
  }, [me?.companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ capabilities, loading, error, refresh }),
    [capabilities, error, loading, refresh],
  );

  return (
    <WorkspaceCapabilitiesContext.Provider value={value}>
      {children}
    </WorkspaceCapabilitiesContext.Provider>
  );
}

export const useWorkspaceCapabilities = () =>
  useContext(WorkspaceCapabilitiesContext);
